/**
 * 「这条片」摘要面板(2026-09-13 D 批,设计稿 7d586023 V4 右栏)—— 目的流两栏 / 自由创作右栏共用。
 *
 * ═══ 装什么 ═══════════════════════════════════════════════════════════════════════
 *   ① 出片比例框:按 `form.ratio` 画真实形状 + 风格底色。**明确标注是出片形状,不是真渲染**
 *      (预览框写「AI 编排后显示」误导过用户;这里从一开始就说清它只是形状示意)。
 *   ② 摘要列:目的 / 拍法 / 时长(目的流带「约 N 镜」)/ 风格 / 比例 —— 一眼看清这条片的配置。
 *   ③ 合身度信号(DurationFitSignal):文案量 × 时长档合不合身,只提示不拦截(目的流才有 ——
 *      自由创作无 briefDuration → durationFit 返 null,自然不渲染)。
 *
 * ★镜数区间 / 内容位数取 `shotBudget` / taxonomy(与创建页各处同 queryKey 缓存),不硬编。
 * ★报价 / 机器参数 / CTA **不在这里** —— 它们由页面装配在面板下方(页面才知道积分怎么算)。
 */

import { ratioLabel } from "@/shared/lib/video/labels";
import type { BriefPurposeDef } from "@/entities/scene-card/model/briefPurpose";
import { ratioToCss } from "@/shared/lib/video/aspect";
import type { Dispatch, SetStateAction } from "react";

import { useCreativeTaxonomy } from "../../model/useCreativeTaxonomy";
import { briefTargetSec } from "../../model/effectiveDuration";
import { durationFit } from "../../model/durationFit";
import { shotBudget } from "../../model/shotBudget";
import type { FormState } from "../../model/videoCreateTypes";

import { DurationFitSignal } from "./DurationFitSignal";
import { DURATION_OPTIONS } from "./VideoFormatRow";
import {
  videoStyleByTone,
  videoStyleLabelForTone,
  videoStylePlaceholderGradient,
  videoStyleStaticUrl,
} from "./videoStyleOptions";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  /** 已选定的有效目的;自由创作传 null(摘要列改用单段时长口径,不显目的/拍法/镜数)。 */
  purpose: BriefPurposeDef | null;
  /** 用户手选的拍法名(没选传 null → 显示「AI 自动选」)。 */
  pickedFormulaName?: string | null;
  disabled?: boolean;
}

export function VideoPlanSummaryPanel({
  form,
  setForm,
  purpose,
  pickedFormulaName,
  disabled,
}: Props) {
  const taxonomyQuery = useCreativeTaxonomy(form.toneRegister);
  const targetSec = briefTargetSec(form.briefDuration);
  const beatPlan =
    purpose && targetSec
      ? (taxonomyQuery.data?.purposes
          ?.find((p) => p.purpose === purpose.label)
          ?.beatPlans?.find((b) => b.targetSec === targetSec) ?? null)
      : null;
  const fit = durationFit(form, beatPlan);

  const styleThumb = videoStyleByTone(form.briefStyle ?? null);
  const previewSrc = styleThumb ? videoStyleStaticUrl(styleThumb) : undefined;
  const styleLabel = videoStyleLabelForTone(form.briefStyle ?? null) ?? "自由发挥";

  // 时长行文案:目的流(编排)= 档名 + 约 N 镜;自由创作(直发)= 单段秒数。
  // ★W4(2026-09-13):没选时长档(含用户主动点「AI 按内容定」,见 VideoDurationTierRow)
  //   统一显「AI 按内容定」——引擎会按文案/卖点量推断,编排后交代卡的「预计成片 Xs」就是答案。
  const tierLabel = DURATION_OPTIONS.find((o) => o.value === form.briefDuration)?.label ?? null;
  const shots = targetSec ? shotBudget(targetSec) : null;
  const durationText = purpose
    ? tierLabel
      ? shots
        ? `${tierLabel} · 约 ${shots[0]}~${shots[1]} 镜`
        : tierLabel
      : "AI 按内容定"
    : `${form.durationSec}s · 单段直发`;

  return (
    <section
      data-testid="plan-summary-panel"
      className="space-y-3 rounded-[calc(var(--radius)*1.15)] border border-border/70 bg-card/60 p-3"
    >
      <div className="text-[13.5px] font-semibold text-foreground">这条片</div>

      {/* ① 出片形状框(风格底色)—— 明确不是真渲染 */}
      <div className="overflow-hidden rounded-lg border bg-black/90">
        <div className="flex justify-center">
          <div
            className="relative flex max-h-[38vh] w-full items-center justify-center overflow-hidden"
            style={{
              aspectRatio: ratioToCss(form.ratio, "9 / 16"),
              background: previewSrc
                ? undefined
                : videoStylePlaceholderGradient(form.briefStyle ?? "free"),
            }}
          >
            {previewSrc && (
              <img
                src={previewSrc}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-30 blur-xl"
              />
            )}
            <span className="relative px-3 text-center text-2xs leading-snug text-white/90">
              {ratioLabel(form.ratio)} 出片形状
              <br />
              示意,非实际画面
            </span>
            <span className="absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 font-mono text-2xs text-white backdrop-blur-sm">
              {ratioLabel(form.ratio)}
            </span>
          </div>
        </div>
      </div>

      {/* ② 摘要列 */}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        {purpose && <Row k="目的" v={purpose.label} />}
        {purpose && <Row k="拍法" v={pickedFormulaName || "AI 自动选"} />}
        <Row k="时长" v={durationText} />
        <Row k="风格" v={styleLabel} />
        <Row k="比例" v={ratioLabel(form.ratio)} />
      </dl>

      {/* ③ 合身度信号(自由创作无 briefDuration → fit 为 null,不渲染) */}
      <DurationFitSignal fit={fit} setForm={setForm} disabled={disabled} />

      {/* ④ 选品没录卖点(2026-09-13 真单 5786997884928 实锤:美妆蛋零卖点 → 引擎只能按
          文案讲、选料/逐镜卖点绑定整组失效)。与合身度同族:琥珀、只提示不拦截。
          判据 = 选了产品 且 该产品带回的卖点为空(briefProducts[].points 全空)。 */}
      {(form.briefProducts?.length ?? 0) > 0 &&
        (form.briefProducts ?? []).every((p) => !(p.points?.length || p.allPoints?.length)) && (
          <p
            data-testid="video-product-no-points-hint"
            className="rounded-md border border-amber-400/40 bg-amber-500/5 px-3 py-2 text-2xs leading-relaxed text-amber-700 dark:border-amber-400/30 dark:text-amber-400"
          >
            选的产品在产品树里还没录卖点 —— AI 只能按你文案里写到的卖点讲。够用,但录了会更准
            (逐镜卖点对号、换选料这些能力都靠它)。补录:产品树 → 该产品 → 卖点。
          </p>
        )}
    </section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-foreground">{v}</dd>
    </>
  );
}
